import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../../shared/material.module';
import { debounceTime, distinctUntilChanged, switchMap, startWith, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { GeolocationService } from '../../../../services/geolocation.service';
import { LanguageService } from '../../../../services/language.service';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { MapComponent, MapType } from '../../../../map/map.component';

@Component({
  selector: 'app-step-accident-details',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    TranslatePipe,
    MapComponent,
  ],
  templateUrl: './step-accident-details.component.html',
  styleUrl: './step-accident-details.component.scss',
})
export class StepAccidentDetailsComponent implements OnInit {
  @ViewChild('mapSection') mapSectionRef!: ElementRef<HTMLElement>;

  @Input() form!: FormGroup;
  @Input() isLoadingLocation = false;
  @Input() incidentLocation: { lat: number, lng: number, address: string } | null = null;
  @Input() currentLocation: { lat: number, lng: number, address: string } | null = null;
  @Input() crashesInfoData: any = null;

  @Output() locationSelected = new EventEmitter<{ lat: number, lng: number, address: string }>();
  @Output() currentLocationChanged = new EventEmitter<{ lat: number, lng: number, address: string }>();

  addressSuggestions$!: Observable<any[]>;
  MapType = MapType;

  scrollToMap(): void {
    this.mapSectionRef?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  constructor(
    private geolocationService: GeolocationService,
    public languageService: LanguageService
  ) { }

  ngOnInit() {
    this.setupAddressAutocomplete();
  }

  private setupAddressAutocomplete() {
    this.addressSuggestions$ = this.form.get('indirizzo')!.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (typeof query === 'string' && query.trim().length >= 3) {
          const cleanQuery = this.filterGenericTerms(query.trim());
          if (cleanQuery.length >= 3) {
            return this.geolocationService.searchAddresses(cleanQuery).pipe(
              catchError(error => {
                console.warn('Errore ricerca indirizzi:', error);
                return of([]);
              })
            );
          }
        }
        return of([]);
      })
    );
  }

  private filterGenericTerms(query: string): string {
    const genericTerms = [
      'via', 'viale', 'corso', 'piazza', 'largo', 'strada', 'vicolo', 'contrada', 'località',
      'street', 'st', 'avenue', 'ave', 'road', 'rd', 'lane', 'ln', 'drive', 'dr', 'boulevard', 'blvd', 'place', 'square', 'sq',
      'rue', 'avenue', 'av', 'boulevard', 'bld', 'place', 'route',
      'calle', 'c/', 'avenida', 'avda', 'plaza', 'carrera', 'camino',
      'straße', 'strasse', 'str', 'platz', 'weg', 'allee',
      'ulica', 'ul.', 'aleja', 'al.', 'plac', 'pl.', 'droga',
      'rruga', 'sheshi', 'bulevardi', 'blv.', 'lagjja',
      'rua', 'av.', 'avenida', 'praça', 'estrada',
      'straat', 'laan', 'plein', 'weg', 'gracht',
      'strada', 'str.', 'bulevard', 'bd.', 'calea', 'piata',
      'odos', 'plateia', 'leoforos',
      'cadde', 'cd.', 'sokak', 'sk.', 'mahalle',
      'roadway', 'highway', 'autostrada', 'motorway'
    ];

    const words = query.toLowerCase().split(/\s+/);
    const filteredWords = words.filter(word => !genericTerms.includes(word));
    return filteredWords.join(' ');
  }

  displayAddress(option: any): string {
    if (typeof option === 'string') return option;
    return option?.formatted_address || option?.display_name || '';
  }

  onAddressSelected(event: any) {
    const selected = event.option.value;
    if (selected && selected.lat && selected.lon) {
      const address = selected.formatted_address || selected.display_name;
      this.incidentLocation = {
        lat: parseFloat(selected.lat),
        lng: parseFloat(selected.lon),
        address: address
      };
      this.form.patchValue({ indirizzo: address });
      this.locationSelected.emit({
        lat: this.incidentLocation.lat,
        lng: this.incidentLocation.lng,
        address: address
      });
    }
  }

  onMapLocationSelected(location: { lat: number, lng: number, address: string }) {
    this.incidentLocation = location;
    this.form.patchValue({ indirizzo: location.address });
    this.locationSelected.emit(location);
  }

  onCurrentLocationChanged(location: { lat: number, lng: number, address: string }) {
    this.currentLocation = location;
    this.form.patchValue({ indirizzoCorrente: location.address });
    this.currentLocationChanged.emit(location);
  }

  getLastPosition(): { lat: number, lng: number, address: string } | null {
    if (this.crashesInfoData?.lastPosition) {
      return {
        lat: this.crashesInfoData.lastPosition.lat,
        lng: this.crashesInfoData.lastPosition.lon,
        address: this.crashesInfoData.lastPosition.address || ''
      };
    }
    return null;
  }
}